import { type RecurringTransactionConfig } from './types';

export type Weekday = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday';

export type Month =
  | 'January'
  | 'February'
  | 'March'
  | 'April'
  | 'May'
  | 'June'
  | 'July'
  | 'August'
  | 'September'
  | 'October'
  | 'November'
  | 'December';

export type AutomaticTransactionFrequency =
  | 'Once'
  | 'Monthly'
  | 'Twice a month'
  | 'Every other week'
  | 'Weekly'
  | 'Annually';

export const MONTHLY_OPTIONS: Record<'value', Month>[] = [
  { value: 'January' },
  { value: 'February' },
  { value: 'March' },
  { value: 'April' },
  { value: 'May' },
  { value: 'June' },
  { value: 'July' },
  { value: 'August' },
  { value: 'September' },
  { value: 'October' },
  { value: 'November' },
  { value: 'December' },
];

export const DAY_OF_WEEK_OPTIONS: Record<'value', Weekday>[] = [
  { value: 'Monday' },
  { value: 'Tuesday' },
  { value: 'Wednesday' },
  { value: 'Thursday' },
  { value: 'Friday' },
];

export function nth(n: number) {
  const suffix = [11, 12, 13].includes(n % 100) ? 'th' : ['st', 'nd', 'rd'][(n % 10) - 1] || 'th';
  return `${n}${suffix}`;
}

interface AutomaticTransactionValues {
  frequency: AutomaticTransactionFrequency;
  month?: Month;
  monthlySelection1?: number;
  monthlySelection2?: number;
  dayOfMonth?: number;
  dayOfWeekSelection?: Weekday;
}

export function frequencyCopy(values: AutomaticTransactionValues) {
  const { frequency, dayOfWeekSelection, month, monthlySelection1, monthlySelection2, dayOfMonth } = values;
  switch (frequency) {
    case 'Monthly':
      return `${nth(monthlySelection1!)} of each month`;
    case 'Twice a month':
      return `${nth(monthlySelection1!)} and ${nth(monthlySelection2!)} of each month`;
    case 'Weekly':
      return `Weekly on ${String(dayOfWeekSelection)}s`;
    case 'Every other week':
      return `Every other ${String(dayOfWeekSelection)}`;
    case 'Annually':
      return `Annually on ${String(month)} ${String(dayOfMonth)}`;
    default:
      return null;
  }
}

export const configFrequencyCopy = ({ frequency }: RecurringTransactionConfig) => {
  switch (frequency.__typename) {
    case 'RecurringTransactionFrequencyAnnually':
      return frequencyCopy({
        frequency: 'Annually',
        month: MONTHLY_OPTIONS[frequency.month - 1]?.value,
        dayOfMonth: frequency.day,
      });
    case 'RecurringTransactionFrequencyMonthly':
      return frequencyCopy({
        frequency: 'Monthly',
        monthlySelection1: frequency.day,
      });
    case 'RecurringTransactionFrequencyTwiceAMonth':
      return frequencyCopy({
        frequency: 'Twice a month',
        monthlySelection1: frequency.firstDay,
        monthlySelection2: frequency.secondDay,
      });
    case 'RecurringTransactionFrequencyWeekly':
      return frequencyCopy({
        frequency: 'Weekly',
        dayOfWeekSelection: DAY_OF_WEEK_OPTIONS[frequency.day - 1]?.value,
      });
    case 'RecurringTransactionFrequencyEveryOtherWeek':
      return frequencyCopy({
        frequency: 'Every other week',
        dayOfWeekSelection: DAY_OF_WEEK_OPTIONS[frequency.day - 1]?.value,
      });
    default:
      return '';
  }
};
